import { commonActions, editActions } from '../actionTypes';
import { ITemplateHistory, IVisualDesignItem, IVisualDesignsSettings, Notification, setNotificationPublishState } from '../../models';
import StepKeys from '../../models/step-keys';
import { cloneNotificationTemplate } from '../helpers/notifications';
import extractVisualDesignDataProps from '../../helpers/visualDesign/extractVisualDesignDataProps';
import mergeVisualDesignDataProps from '../../helpers/visualDesign/mergeVisualDesignDataProps';
import { INotification, IVisualDesignData } from '@communication-center/data-objects';
import _isEqual from 'lodash/isEqual';
import _truncate from 'lodash/truncate';

import { RuntimeFieldMaxLength } from '../../components/EditNotification/EditNotificationContent/SendStepDrawer/NotificationTypePicker/RuntimeField/RuntimeField';

function buildTemplateHistory(
    currentTemplateHistory: ITemplateHistory | undefined,
    visualDesignData: IVisualDesignData,
    { templateId, templates }: { templateId?: string; templates?: IVisualDesignItem[] }
): ITemplateHistory {
    const { image, title, message, dependencies } = extractVisualDesignDataProps(visualDesignData);
    let imageProps = {};
    let baseTemplateImage = null;

    const baseTemplate = templates?.find?.(({ id }) => id === templateId);

    if (baseTemplate) {
        ({ image: baseTemplateImage } = extractVisualDesignDataProps(baseTemplate.template));
    }

    if (baseTemplateImage) {
        if (image && baseTemplateImage?.src !== image?.src) {
            image.aspectRatio = image.aspectRatio ?? currentTemplateHistory?.image?.aspectRatio;
            imageProps = { image };
        } else {
            imageProps = { image: null };
        }
    }

    return {
        ...currentTemplateHistory,
        title,
        message,
        ...imageProps,
        dependencies
    };
}

export interface IEditNotificationState {
    notification?: Notification;
    visualDesignsSettings?: IVisualDesignsSettings;
    filteredVisualDesigns?: IVisualDesignItem[];
    currentStep: number;
    dirty?: boolean;
    templateHistory?: ITemplateHistory;
    trackVisualDesignTitle: boolean;
    trackVisualDesignDescription: boolean;
    showTemplates: boolean;
    hideTemplatesButton: boolean;
}

export const editNotificationInitialState: IEditNotificationState = {
    notification: undefined,
    visualDesignsSettings: undefined,
    filteredVisualDesigns: undefined,
    currentStep: StepKeys.Themes,
    dirty: false,
    templateHistory: undefined,
    trackVisualDesignTitle: false,
    trackVisualDesignDescription: false,
    showTemplates: false,
    hideTemplatesButton: false
};

export default function editNotificationReducer(
    state = editNotificationInitialState,
    action: { type: $TSFixMe; payload: $TSFixMe; error?: boolean }
): IEditNotificationState {
    switch (action.type) {
        case editActions.SET_EDIT_NOTIFICATION: {
            let templateHistory: ITemplateHistory | undefined = undefined;
            let trackVisualDesignTitle = true;
            let trackVisualDesignDescription = true;

            if (action.payload.id) {
                templateHistory = buildTemplateHistory(state.templateHistory, action.payload.visualDesignData, {
                    templateId: action.payload.visualDesignTemplate,
                    templates: state.filteredVisualDesigns
                });

                // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | boolean | undefined' is not assigna... Remove this comment to see the full error message
                trackVisualDesignTitle =
                    templateHistory.title?.text && templateHistory.title?.text.substring(0, RuntimeFieldMaxLength) === action.payload?.runtimeTitle;

                // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | boolean | undefined' is not assigna... Remove this comment to see the full error message
                trackVisualDesignDescription =
                    templateHistory.message?.text &&
                    templateHistory.message?.text.substring(0, RuntimeFieldMaxLength) === action.payload?.runtimeDescription;
            }

            return {
                ...state,
                notification: action.payload,
                dirty: false,
                templateHistory,
                trackVisualDesignTitle,
                trackVisualDesignDescription
            };
        }

        case editActions.DUPLICATE_NOTIFICATION: {
            const notification = action.payload;

            return {
                ...state,
                notification,
                dirty: true,
                templateHistory: buildTemplateHistory(state.templateHistory, notification.visualDesignData, {
                    templateId: notification.visualDesignTemplate,
                    templates: state.filteredVisualDesigns
                }),
                currentStep: StepKeys.Design
            };
        }

        case editActions.SET_TRACK_VISUAL_DESIGN_TITLE: {
            const { shouldTrack } = action.payload;
            return { ...state, trackVisualDesignTitle: shouldTrack };
        }

        case editActions.SET_TRACK_VISUAL_DESIGN_DESCRIPTION: {
            const { shouldTrack } = action.payload;
            return { ...state, trackVisualDesignDescription: shouldTrack };
        }

        case editActions.SET_NOTIFICATION_TEMPLATE: {
            const { visualDesignItem } = action.payload;

            const template = state.templateHistory
                ? mergeVisualDesignDataProps(visualDesignItem.template, state.templateHistory)
                : visualDesignItem.template;

            const templateHistory = buildTemplateHistory(state.templateHistory, template, {
                templateId: visualDesignItem.id,
                templates: state.filteredVisualDesigns
            });

            const visualDesignData = cloneNotificationTemplate(template);

            const { message } = extractVisualDesignDataProps(visualDesignData);

            const notification = {
                ...state.notification,
                visualDesignTemplate: visualDesignItem.id,
                visualDesignData,
                message: message?.text
            };

            return { ...state, notification, templateHistory };
        }

        case editActions.NOTIFICATION_FIELD_VALUE_CHANGED: {
            const changes = action.payload;

            let { templateHistory } = state;

            let notification: INotification = {
                ...state.notification,
                ...changes
            };

            if (changes.visualDesignData) {
                templateHistory = buildTemplateHistory(state.templateHistory, changes.visualDesignData, {
                    templateId: notification.visualDesignTemplate,
                    templates: state.filteredVisualDesigns
                });

                notification = {
                    ...notification,
                    message: templateHistory.message?.text,
                    dependencies: templateHistory.dependencies
                };

                const titleChanged = state.templateHistory && !_isEqual(templateHistory.title?.text, state.templateHistory.title?.text);
                const messageChanged = state.templateHistory && !_isEqual(templateHistory.message?.text, state.templateHistory.message?.text);

                if (state.trackVisualDesignTitle && titleChanged) {
                    notification.runtimeTitle = templateHistory.title?.text && templateHistory.title?.text.substring(0, RuntimeFieldMaxLength);
                }

                if (state.trackVisualDesignDescription && messageChanged) {
                    const shortenedDescription =
                        templateHistory.message?.text &&
                        _truncate(templateHistory.message?.text, {
                            length: RuntimeFieldMaxLength,
                            separator: /(,|\.)? +/
                        });

                    notification.runtimeDescription = shortenedDescription;
                }
            }

            return { ...state, notification, templateHistory, dirty: true };
        }

        case editActions.PUBLISHED_STATE_FIELD_VALUE_CHANGED: {
            const { environmentId, properties } = action.payload;
            const notification = { ...state.notification };
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            const publishState = notification?.publishStates.find(ps => ps.environmentId == environmentId);
            setNotificationPublishState(notification, { ...publishState, ...properties });
            return { ...state, notification: notification };
        }

        case editActions.SCHEDULING_STATE_FIELD_VALUE_CHANGED: {
            const { environmentId, properties } = action.payload;
            const notification = { ...state.notification };
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            const publishState = notification?.publishStates.find(ps => ps.environmentId == environmentId);
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            setNotificationPublishState(notification, { ...publishState, scheduling: { ...publishState.scheduling, ...properties } });
            return { ...state, notification: notification };
        }

        case editActions.LOAD_VISUAL_DESIGN_SETTINGS: {
            const settings = action.payload;
            const categoryId = settings.categories[0].id;
            const filteredDesigns = settings.visualDesigns?.filter((design: { categories?: string[] }) => {
                return design.categories?.includes(categoryId);
            });
            return {
                ...state,
                visualDesignsSettings: settings,
                filteredVisualDesigns: filteredDesigns
            };
        }

        case editActions.SET_STEP:
            return { ...state, currentStep: action.payload };

        case editActions.SET_SHOW_TEMPLATES:
            return { ...state, showTemplates: action.payload };

        case editActions.SET_HIDE_TEMPLATES_BUTTON:
            return { ...state, hideTemplatesButton: action.payload };

        case commonActions.ENVIRONMENT_CHANGED:
        case commonActions.SYSTEM_CHANGED: {
            const { visualDesignsSettings, filteredVisualDesigns } = state;
            return {
                ...editNotificationInitialState,
                visualDesignsSettings,
                filteredVisualDesigns
            };
        }

        case editActions.RESET_EDIT: {
            return {
                ...editNotificationInitialState,
                visualDesignsSettings: state.visualDesignsSettings,
                filteredVisualDesigns: state.filteredVisualDesigns
            };
        }

        default:
            return state;
    }
}
